export const FILES_VIEW_MODE_KEY = 'fastchat.filesViewMode';

export function readStoredViewMode() {
  try {
    return localStorage.getItem(FILES_VIEW_MODE_KEY) === 'details' ? 'details' : 'thumbs';
  } catch {
    return 'thumbs';
  }
}

export function persistViewMode(viewMode) {
  try {
    localStorage.setItem(FILES_VIEW_MODE_KEY, viewMode);
  } catch {
    /* ignore */
  }
}

export const SHOW_SUBFOLDERS_KEY = 'fastchat.filesShowSubfolders';

export function readStoredShowSubfolders() {
  try {
    return localStorage.getItem(SHOW_SUBFOLDERS_KEY) === '1';
  } catch {
    return false;
  }
}

export function persistShowSubfolders(value) {
  try {
    localStorage.setItem(SHOW_SUBFOLDERS_KEY, value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function groupFilesByFolder(files, rootFolderId) {
  const groups = [];
  for (const file of files || []) {
    const id = file.folderId || rootFolderId;
    const last = groups[groups.length - 1];
    if (last && last.folderId === id) {
      last.files.push(file);
    } else {
      groups.push({
        folderId: id,
        folderName: file.folderName || '',
        isRoot: id === rootFolderId,
        files: [file],
      });
    }
  }
  return groups;
}

export function formatFileDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

export function compareFiles(a, b, key, dir) {
  const mul = dir === 'desc' ? -1 : 1;
  if (key === 'fileName') {
    return mul * String(a.fileName || '').localeCompare(String(b.fileName || ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }
  if (key === 'fileSize') {
    return mul * ((a.fileSize || 0) - (b.fileSize || 0));
  }
  if (key === 'uploadedAt') {
    return mul * (new Date(a.uploadedAt || 0) - new Date(b.uploadedAt || 0));
  }
  return mul * ((a.sortIndex || 0) - (b.sortIndex || 0));
}

export const FILE_DRAG_MIME = 'application/x-fastchat-file';
export const FILE_DRAG_PREFIX = 'fastchat-file:';

export function encodeFileDragPayload({ folderId, storedNames }) {
  return `${FILE_DRAG_PREFIX}${JSON.stringify({ folderId, storedNames })}`;
}

export function parseFileDragPayload(raw) {
  if (!raw || !String(raw).startsWith(FILE_DRAG_PREFIX)) return null;
  const rest = String(raw).slice(FILE_DRAG_PREFIX.length);
  try {
    const data = JSON.parse(rest);
    if (!data || typeof data.folderId !== 'string' || !Array.isArray(data.storedNames)) return null;
    return {
      folderId: data.folderId,
      storedNames: data.storedNames.filter((name) => typeof name === 'string' && name),
    };
  } catch {
    return rest ? { folderId: '', storedNames: [rest] } : null;
  }
}

export function isFileDragEvent(event) {
  const types = Array.from(event.dataTransfer?.types || []).map((t) => String(t).toLowerCase());
  if (types.includes(FILE_DRAG_MIME)) return true;
  const hasText = types.includes('text/plain') || types.includes('text');
  return hasText && !types.includes('files');
}

export function describeSkippedMoves(skipped) {
  if (!skipped?.length) return '';
  const lines = skipped.slice(0, 10).map((item) => {
    const name = item.fileName || item.storedName || 'file';
    if (item.reason === 'name_exists') return `${name} already exists in that folder`;
    if (item.reason === 'not_found') return `${name} was not found`;
    return name;
  });
  const extra = skipped.length > 10 ? `\nand ${skipped.length - 10} more` : '';
  return `${skipped.length} file${skipped.length === 1 ? '' : 's'} could not be moved:\n${lines.join('\n')}${extra}`;
}

export async function moveDraggedFilesToFolder({ event, targetFolderId, moveFiles }) {
  event.preventDefault();
  event.stopPropagation();
  const payload = parseFileDragPayload(event.dataTransfer.getData('text/plain') || '');
  if (!payload?.storedNames?.length) return null;
  if (payload.folderId === targetFolderId) return { moved: [], skipped: [] };
  const res = await moveFiles(payload.folderId, targetFolderId, payload.storedNames);
  return res.data || { moved: [], skipped: [] };
}
